import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ParentDocumentHubService, UploadedDocument } from './parent-document-hub.service';

type DocFilter = 'ALL' | 'PDF' | 'IMAGE' | 'DOC';
type MappedDocType = 'pdf' | 'image' | 'doc';

export interface DocCard {
  id: string;
  name: string;
  date: string;
  createdBy: string;
  type: MappedDocType;
  url: string;
}

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];

// Document Hub — read-only list of documents the school has uploaded for
// the student (DocumentHub.js). Search + type-filter pills, both purely
// client-side (the API has no query params besides lang — see
// parent-document-hub.service.ts). No @Input(studentId): the backend
// resolves the student from the auth token, same as leave-request, so this
// reloads on studentSwitchTick instead of a studentId/classId change.
@Component({
  selector: 'app-parent-document-hub',
  templateUrl: './parent-document-hub.component.html',
  styleUrls: ['./parent-document-hub.component.css'],
})
export class ParentDocumentHubComponent implements OnInit, OnChanges {

  @Input() studentSwitchTick = 0;

  loading = false;
  error = '';
  documents: DocCard[] = [];

  searchTerm = '';
  activeFilter: DocFilter = 'ALL';

  // In-app preview — image (small centered window, same pattern as diary's
  // media viewer) or PDF (iframe). A generic "doc" type has no in-app
  // viewer at all (per the API reference: "Cannot preview this file"), so
  // it shows a Cancel/Download choice instead.
  previewImageUrl: string | null = null;
  previewPdfUrl: SafeResourceUrl | null = null;
  unsupportedDoc: DocCard | null = null;

  private requestId = 0;

  constructor(private docService: ParentDocumentHubService, private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    this.loadDocuments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studentSwitchTick'] && !changes['studentSwitchTick'].firstChange) {
      this.searchTerm = '';
      this.activeFilter = 'ALL';
      this.closePreview();
      this.loadDocuments();
    }
  }

  private get lang(): string {
    // Deliberately always 'en', not the UI's selected display language —
    // the parent mobile app's backend endpoints expect this constant
    // regardless of what language the user has the interface in; switching
    // the UI language must never change this API payload field.
    return 'en';
  }

  loadDocuments(): void {
    this.loading = true;
    this.error = '';
    const requestId = ++this.requestId;
    this.docService.getUploadedDocuments(this.lang).subscribe({
      next: (res: any) => {
        if (requestId !== this.requestId) { return; }
        this.loading = false;
        const list: UploadedDocument[] = (res && res.status && res.body && res.body.uploadedDocumentsList) || [];
        this.documents = list.map(d => this.mapDocument(d));
      },
      error: () => {
        if (requestId !== this.requestId) { return; }
        this.loading = false;
        this.documents = [];
        this.error = 'Unable to load documents.';
      },
    });
  }

  private mapDocument(d: UploadedDocument): DocCard {
    return {
      id: String(d.pkStudentDocumentsId),
      name: d.documentTitle || d.documentTypeName || '',
      date: d.createdOn,
      createdBy: d.createdBy,
      type: this.classifyType(d.fileType),
      url: d.documentUrl,
    };
  }

  // getDocumentType: "pdf" -> pdf; "image" or a known image extension ->
  // image; anything else -> generic "doc" (per the API reference).
  private classifyType(fileType: string): MappedDocType {
    const ft = (fileType || '').toLowerCase();
    if (ft === 'pdf') { return 'pdf'; }
    if (ft === 'image' || IMAGE_EXTENSIONS.includes(ft)) { return 'image'; }
    return 'doc';
  }

  get filteredDocuments(): DocCard[] {
    const term = this.searchTerm.trim().toLowerCase();
    return this.documents.filter(d => {
      const matchesSearch = !term || d.name.toLowerCase().includes(term);
      const matchesFilter = this.activeFilter === 'ALL'
        || (this.activeFilter === 'PDF' && d.type === 'pdf')
        || (this.activeFilter === 'IMAGE' && d.type === 'image')
        || (this.activeFilter === 'DOC' && d.type === 'doc');
      return matchesSearch && matchesFilter;
    });
  }

  get countAll(): number { return this.documents.length; }
  get countPdf(): number { return this.documents.filter(d => d.type === 'pdf').length; }
  get countImage(): number { return this.documents.filter(d => d.type === 'image').length; }
  get countDoc(): number { return this.documents.filter(d => d.type === 'doc').length; }

  setFilter(filter: DocFilter): void {
    this.activeFilter = filter;
  }

  iconClass(doc: DocCard): string {
    if (doc.type === 'pdf') { return 'ri-file-pdf-2-line'; }
    if (doc.type === 'image') { return 'ri-image-line'; }
    return 'ri-file-text-line';
  }

  openDocument(doc: DocCard): void {
    if (doc.type === 'image') {
      this.previewImageUrl = doc.url;
      return;
    }
    if (doc.type === 'pdf') {
      this.previewPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(doc.url);
      return;
    }
    // Generic "doc" type — no in-app viewer, matches the mobile app's own
    // "Cannot preview this file" Cancel/Download popup.
    this.unsupportedDoc = doc;
  }

  closePreview(): void {
    this.previewImageUrl = null;
    this.previewPdfUrl = null;
    this.unsupportedDoc = null;
  }

  downloadUnsupportedDoc(): void {
    if (this.unsupportedDoc) {
      window.open(this.unsupportedDoc.url, '_blank', 'noopener');
    }
    this.closePreview();
  }
}
